import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { InputIcon } from "../../../common/components/Input";
import { LoadingState } from "../../../common/state/slice/appStateSlice";
import type { ISetPasswordFormData } from "../state/authSlice";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import { Error } from "../../../common/components/AppState/Error";
import { SET_PASSWORD_COMPONENTID } from "../../../pages/Login/SetPassword";
import { validatePassword, getPasswordStrength } from "../../../common/utils/passwordValidation";

interface ISetPasswordForm {
  onSubmit: (data: ISetPasswordFormData) => void;
  loading?: LoadingState;
  showSuccessMessage?: boolean;
}

const SetPasswordForm = ({ onSubmit, loading, showSuccessMessage }: ISetPasswordForm) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ISetPasswordFormData>();

  const [passwordVisible, setPasswordVisible] = useState(false);
  const password = watch("password", "");

  const passwordStrength = password ? getPasswordStrength(password, t) : null;
  const validationResult = password ? validatePassword(password, t) : null;

  const getStrengthColor = () => {
    if (!passwordStrength) return "bg-gray-300";
    if (passwordStrength === "weak") return "bg-red-500";
    if (passwordStrength === "medium") return "bg-yellow-500";
    return "bg-green-500";
  };

  const getStrengthWidth = () => {
    if (!passwordStrength) return "0%";
    if (passwordStrength === "weak") return "33%";
    if (passwordStrength === "medium") return "66%";
    return "100%";
  };

  return (
    <div className="bg-slate-100 flex h-screen">
      <div className="flex flex-1 max-w-xs md:max-w-lg mx-auto items-center justify-center ">
        <form className="w-full sm:w-3/4" onSubmit={handleSubmit(onSubmit)}>
          <div className="border flex flex-col bg-white border-gray-200 rounded-lg shadow-md py-3 px-4">
            <div className="my-2">
              <h2 className="text-xl font-medium text-black">
                {t("pages.setPassword.title", "Créer votre mot de passe")}
              </h2>
            </div>

            <Error componentId={SET_PASSWORD_COMPONENTID} />
            <IsLoading componentId={SET_PASSWORD_COMPONENTID} showSuccess={false} />

            {showSuccessMessage && (
              <div className="text-green-600 bg-green-50 border border-green-200 rounded p-2 mb-3">
                {t(
                  "pages.setPassword.success",
                  "Mot de passe créé avec succès. Redirection vers la connexion...",
                )}
              </div>
            )}

            {errors.passwordConfirmation && (
              <span className="text-red-500">{errors.passwordConfirmation.message}</span>
            )}

            <InputIcon
              registerFct={() =>
                register("password", {
                  required: true,
                  validate: (val: string) => validatePassword(val, t),
                })
              }
              type={passwordVisible ? "text" : "password"}
              position="right"
              label={t("pages.setPassword.enterPassword", "Nouveau mot de passe")}
              placeholder={t("pages.setPassword.enterPassword", "Nouveau mot de passe")}
              handleClick={() => setPasswordVisible(!passwordVisible)}
              icon={passwordVisible ? "EyeOffIcon" : "EyeIcon"}
            />

            {password && validationResult !== true && (
              <div className="text-sm text-red-500 mt-1 mb-2">{validationResult}</div>
            )}

            {password && (
              <div className="my-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600">
                    {t("pages.setPassword.passwordStrength", "Force du mot de passe")}:
                  </span>
                  <span className="text-sm font-medium">
                    {passwordStrength === "weak" && t("pages.setPassword.weak", "Faible")}
                    {passwordStrength === "medium" && t("pages.setPassword.medium", "Moyen")}
                    {passwordStrength === "strong" && t("pages.setPassword.strong", "Fort")}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor()}`}
                    style={{ width: getStrengthWidth() }}
                  ></div>
                </div>
              </div>
            )}

            <InputIcon
              registerFct={() =>
                register("passwordConfirmation", {
                  required: true,
                  validate: (val: string) => {
                    if (watch("password") !== val) {
                      return t(
                        "pages.setPassword.passwordNotMatch",
                        "Les mots de passe ne correspondent pas",
                      );
                    }
                    return true;
                  },
                })
              }
              type={passwordVisible ? "text" : "password"}
              position="right"
              label={t("pages.setPassword.confirmPassword", "Confirmer le mot de passe")}
              placeholder={t("pages.setPassword.confirmPassword", "Confirmer le mot de passe")}
              handleClick={() => setPasswordVisible(!passwordVisible)}
              icon={passwordVisible ? "EyeOffIcon" : "EyeIcon"}
            />

            <div className="flex flex-col sm:flex-row sm:pt-8 sm:justify-between">
              <button
                className="btn-primary-fill"
                type="submit"
                disabled={loading === LoadingState.Loading}
              >
                {t("pages.setPassword.confirm", "Créer mon mot de passe")}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetPasswordForm;
